import { useContext, useState, useEffect, useMemo } from "react";
import { Card, OverlayTrigger, Tooltip } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import { ThemeContext } from "../../../../theme/ThemeContext";
import AuthContext from "../../../../auth/AuthContext";
import { Skeleton } from "primereact";
import Moment from "moment";
import { FiArrowRight } from "react-icons/fi";
import CourseService from "../../../../services/CourseService";

const CourseCalender = () => {
  const [date, setDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [courseFilter, setCourseFilter] = useState("");

  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const authContext = useContext(AuthContext);

  //Calendar range constants 
  const CURRENT_YEAR = new Date().getFullYear();
  const MIN_YEAR     = 2014;
  const MIN_MONTH    = 0;   // January
  const MAX_YEAR     = CURRENT_YEAR + 5;
  const MAX_MONTH    = 11;  // December

  const tooltipBg = "#ffffff";
  const tooltipText = "#000000";
  const tooltipBorder = isDark ? "#555" : "#ddd";

  const tooltipStyles = `
    .tooltip-inner {
      background-color: ${tooltipBg} !important;
      color: ${tooltipText} !important;
      border: 1px solid ${tooltipBorder};
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      font-size: 10px;
    }
    .tooltip.bs-tooltip-top .tooltip-arrow::before {
      border-top-color: ${tooltipBg} !important;
    }
    .tooltip.bs-tooltip-bottom .tooltip-arrow::before {
      border-bottom-color: ${tooltipBg} !important;
    }
    .tooltip.bs-tooltip-left .tooltip-arrow::before {
      border-left-color: ${tooltipBg} !important;
    }
    .tooltip.bs-tooltip-right .tooltip-arrow::before {
      border-right-color: ${tooltipBg} !important;
    }
    .tooltip { opacity: 1 !important; }
  `;

  // Calendar constants with three-letter month names
  const monthNames = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  // Color palette for courses
  const courseDefaultColor = "#F5A623";
  const courseColorsPalette = useMemo(
    () => [
      "#5fbfe6", "#9B59B6", "rgba(24, 104, 136, 1)", "#F5A623",
      "#43A047", "#4A90E2", "#7b1fa2", "#ff5722",
      "#512da8", "#8bc34a", "#f06292",
    ],
    []
  );

  // Years array for dropdown
  const years = useMemo(() => {
    const arr = [];
    for (let y = MIN_YEAR; y <= MAX_YEAR; y++) {
      arr.push(y);
    }
    return arr;
  }, [MIN_YEAR, MAX_YEAR]);

  // Memoized list of courses for the current month
  const monthlyCourses = useMemo(() => {
    return [
      ...new Set(
        events
          .filter((ev) => ev.type?.toLowerCase() === "course")
          .filter(
            (ev) =>
              new Date(ev.plannedEndDate) >= new Date(date.getFullYear(), date.getMonth(), 1) &&
              new Date(ev.plannedStartDate) <= new Date(date.getFullYear(), date.getMonth() + 1, 0)
          )
          .map((ev) => ({ id: ev.id, name: ev.name }))
      ),
    ].sort((a, b) => a.name.localeCompare(b.name));
  }, [events, date]);

  // Memoized course color mapping
  const courseColorMap = useMemo(() => {
    if (monthlyCourses.length === 0) return {};
    const map = {};
    monthlyCourses.forEach((c, idx) => {
      map[c.id] = courseColorsPalette[idx % courseColorsPalette.length];
    });
    return map;
  }, [monthlyCourses, courseColorsPalette]);

  const getCourseColor = (courseId) => courseColorMap[courseId] || courseDefaultColor;

  // Month navigation guards
  const isPrevMonthDisabled = useMemo(() => {
    return date.getFullYear() === MIN_YEAR && date.getMonth() === MIN_MONTH;
  }, [date, MIN_YEAR, MIN_MONTH]);

  const isNextMonthDisabled = useMemo(() => {
    return date.getFullYear() === MAX_YEAR && date.getMonth() === MAX_MONTH;
  }, [date, MAX_YEAR, MAX_MONTH]);

  // Handlers 
  const handleYearChange = (e) => {
    const selectedYear = Number(e.target.value);
    setDate((prev) => {
      let month = prev.getMonth();
      if (selectedYear === MIN_YEAR && month < MIN_MONTH) {
        month = MIN_MONTH;
      }
      return new Date(selectedYear, month, 1);
    });
  };

  const changeMonth = (offset) => {
    setDate((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + offset);

      const year = newDate.getFullYear();
      const month = newDate.getMonth();

      if (year < MIN_YEAR || (year === MIN_YEAR && month < MIN_MONTH)) {
        return prev;
      }
      if (year > MAX_YEAR || (year === MAX_YEAR && month > MAX_MONTH)) {
        return prev;
      }

      return newDate;
    });
  };

  // Get days in the current month
  const getDaysInMonth = (year, month) => {
    const firstDay = new Date(year, month, 1).getDay();
    const totalDays = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let i = 1; i <= totalDays; i++) cells.push(i);
    return cells;
  };

  // Fetch calendar data
useEffect(() => {
    const controller = new AbortController(); 

    const fetchCalendarData = async () => {
      setLoading(true);
      try {
        const startDate = Moment(new Date(date.getFullYear(), date.getMonth(), 1)).format("YYYY-MM-DD");
        const endDate = Moment(new Date(date.getFullYear(), date.getMonth() + 1, 0)).format("YYYY-MM-DD");

        const response = await CourseService.GetCalenderChart(
          authContext,
          startDate,
          endDate,
          controller.signal 
        );

        if (controller.signal.aborted) return; 

        if (response?.isSuccess && Array.isArray(response.data)) {
          setEvents(response.data);
        } else {
          setEvents([]);
        }
      } catch (error) {
        if (error.name === "AbortError") return;
        console.error("Failed to fetch calendar data:", error);
        setEvents([]);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    fetchCalendarData();

    return () => controller.abort();
  }, [authContext, date]);

  // Filter events
  const filteredEvents = useMemo(
    () =>
      events.filter(
        (ev) =>
          ev.type?.toLowerCase() === "course" &&
          (courseFilter === "" || ev.id.toString() === courseFilter)
      ),
    [events, courseFilter]
  );

  const courses = useMemo(
    () => events.filter((ev) => ev.type?.toLowerCase() === "course"),
    [events]
  );

  //  Tooltip & Highlight Logic
  const renderTooltip = (ev, type, currentDate) => {
    if (type !== "course") return null;

    const start = new Date(ev.plannedStartDate);
    const end = new Date(ev.plannedEndDate);
    const color = getCourseColor(ev.id);
    const isStart = currentDate && currentDate.toDateString() === start.toDateString();
    const isEnd = currentDate && currentDate.toDateString() === end.toDateString();

    return (
      <div
        key={`tooltip-course-${ev.id}-${isStart ? "start" : isEnd ? "end" : "mid"}`}
        style={{ fontSize: 11, minWidth: 160 }}
      >
        <div style={{ marginBottom: 4 }}>
          <strong style={{ color, textTransform: "capitalize", fontSize: 12 }}>
            {ev.name}
          </strong>
        </div>
        <div style={{ color: "#666" }}>
          {isStart ? "Start: " : isEnd ? "End: " : "In Progress "}
          {start.toLocaleDateString("en-GB")} <FiArrowRight color={color} />{" "}
          {end.toLocaleDateString("en-GB")}
        </div>
      </div>
    );
  };

  const getDayEventHighlight = (dateObj) => {
    const coursesOnDate = filteredEvents.filter(
      (ev) =>
        dateObj >= new Date(ev.plannedStartDate) &&
        dateObj <= new Date(ev.plannedEndDate)
    );

    if (coursesOnDate.length === 0) {
      return { highlightType: null, bgColor: null, tooltipData: [] };
    }

    const startCourses = [];
    const endCourses = [];
    const middleCourses = [];

    coursesOnDate.forEach((ev) => {
      const start = new Date(ev.plannedStartDate);
      const end = new Date(ev.plannedEndDate);
      if (dateObj.toDateString() === start.toDateString()) startCourses.push(ev);
      else if (dateObj.toDateString() === end.toDateString()) endCourses.push(ev);
      else middleCourses.push(ev);
    });

    let bgColor = "transparent";

    if (startCourses.length > 1) {
      const colors = startCourses.map((ev) => getCourseColor(ev.id));
      bgColor = `linear-gradient(90deg, ${colors
        .map((c, i) => `${c} ${i * (100 / colors.length)}%, ${c} ${(i + 1) * (100 / colors.length)}%`)
        .join(", ")})`;
    } else if (startCourses.length === 1) {
      bgColor = getCourseColor(startCourses[0].id);
    } else if (endCourses.length > 1) {
      const colors = endCourses.map((ev) => getCourseColor(ev.id));
      bgColor = `linear-gradient(90deg, ${colors
        .map((c, i) => `${c} ${i * (100 / colors.length)}%, ${c} ${(i + 1) * (100 / colors.length)}%`)
        .join(", ")})`;
    } else if (endCourses.length === 1) {
      bgColor = getCourseColor(endCourses[0].id);
    } else if (middleCourses.length === 1) {
      bgColor = getCourseColor(middleCourses[0].id) + "33";
    }

    const tooltipData = coursesOnDate.map((ev) => renderTooltip(ev, "course", dateObj));

    return { highlightType: "course", bgColor, tooltipData };
  };

  const cells = getDaysInMonth(date.getFullYear(), date.getMonth());

  return (
    <>
      <style>{tooltipStyles}</style>
      <Card
        className="p-3 charts"
        style={{
          height: "280px",
          borderRadius: 12,
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
          border: "1px solid #eee",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          gap: "6px",
        }}
      >
        <div
          className="d-flex justify-content-between align-items-center mb-2 px-1 gap-2"
          style={{ fontSize: 12 }}
        >
          <div style={{ minWidth: 100 }}>
            {loading ? (
              <Skeleton width="100px" height="30px" />
            ) : (
              <select
                value={courseFilter}
                onChange={(e) => setCourseFilter(e.target.value)}
                style={{
                  maxWidth: "150px",
                  backgroundColor: isDark ? "#222" : "#fff",
                  color: isDark ? "#eaeaea" : "#000",
                  fontSize: 12,
                  border: "1px solid #ddd",
                  borderRadius: 4,
                  padding: "4px 8px",
                }}
              >
                <option value="">All courses</option>
                {courses.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            )}
          </div>
          <button
            className="btn btn-light btn-sm py-0 px-1"
            onClick={() => changeMonth(-1)}
            disabled={loading || isPrevMonthDisabled}
            title={isPrevMonthDisabled ? "Cannot go before Jan 2014" : ""}
          >
            &lt;
          </button>

   <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              justifyContent: "center",
            }}
          >
            {loading ? (
              <Skeleton width="60px" height="30px" />
            ) : (
              <span style={{ fontWeight: 500 }}>
                {monthNames[date.getMonth()]}
              </span>
            )}

            {loading ? (
              <Skeleton width="70px" height="30px" />
            ) : (
              <select
                value={date.getFullYear()}
                onChange={handleYearChange}
                disabled={loading}
                style={{
                  fontSize: 12,
                  padding: "4px 8px",
                  borderRadius: 4,
                  border: "1px solid #ddd",
                  backgroundColor: isDark ? "#222" : "#fff",
                  color: isDark ? "#eaeaea" : "#000",
                }}
              >
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            )}
          </div>

          <button
            className="btn btn-light btn-sm py-0 px-1"
            onClick={() => changeMonth(1)}
            disabled={loading || isNextMonthDisabled}
            title={isNextMonthDisabled ? `Cannot go beyond Dec ${MAX_YEAR}` : ""}
          >
            &gt;
          </button>
        </div>

        {loading ? (
          <div style={{ flexGrow: 1, display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "6px" }}>
            {days.map((_, i) => (
              <Skeleton key={`day-skel-${i}`} width="100%" height="20px" />
            ))}
            {Array.from({ length: 35 }).map((_, i) => (
              <Skeleton key={`cell-skel-${i}`} width="100%" height="30px" />
            ))}
          </div>
        ) : (
          <div
            className="calendar-grid text-center"
            style={{
              flexGrow: 1,
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gap: "3px",
              fontSize: 9,
              height: "100%",
            }}
          >
            {days.map((day) => (
              <div
                key={day}
                className="fw-bold"
                style={{ color: isDark ? "#eaeaea" : "#333", fontSize: 9 }}
              >
                {day}
              </div>
            ))}

            {cells.map((cell, idx) => {
              if (!cell) return <div key={idx} style={{ height: "100%" }} />;

              const fullDate = new Date(date.getFullYear(), date.getMonth(), cell);
              const { bgColor, tooltipData } = getDayEventHighlight(fullDate);

              const coursesOnDate = filteredEvents.filter(
                (ev) =>
                  fullDate >= new Date(ev.plannedStartDate) &&
                  fullDate <= new Date(ev.plannedEndDate)
              );

              const textColor = isDark ? "#eaeaea" : "#000";

              return (
                <OverlayTrigger
                  key={idx}
                  placement="top"
                  overlay={
                    tooltipData.length > 0 ? (
                      <Tooltip id={`tooltip-${idx}`}>
                        <div>{tooltipData}</div>
                      </Tooltip>
                    ) : (
                      <Tooltip id={`tooltip-${idx}`} style={{ display: "none" }} />
                    )
                  }
                >
                  <div
                    className="calendar-cell rounded"
                    style={{
                      background: bgColor || "transparent",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 10,
                      height: 30,
                      color: textColor,
                      border: "none",
                      overflow: "hidden",
                      padding: "2px 4px",
                      minHeight: 30,
                    }}
                  >
                    <div>{cell}</div>
                    <div style={{ display: "flex", gap: 2, marginTop: 2, justifyContent: "center", flexWrap: "wrap" }}>
                      {coursesOnDate.map((course) => (
                        <span
                          key={`dot-${course.id}`}
                          style={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            backgroundColor: getCourseColor(course.id),
                            border: "1px solid #fff",
                            boxShadow: "0 0 2px rgba(0,0,0,0.15)",
                          }}
                          title={course.name}
                        />
                      ))}
                    </div>
                  </div>
                </OverlayTrigger>
              );
            })}
          </div>
        )}
      </Card>
    </>
  );
};

export default CourseCalender;